import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import axios from 'axios'
import HumanBody3D from './HumanBody3D'
import { INJURIES, LAYER_META } from '../data/injuries'
import { useCaleb } from '../context/CalebContext'

const API  = process.env.REACT_APP_API_URL || 'http://localhost:8000'
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const TEAL        = '#0d9488'
const TEAL_LIGHT  = '#14b8a6'
const TEAL_DEEP   = '#065f46'

//  Viewport hook 
function useViewport() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200)
  useEffect(() => {
    const h = () => setW(window.innerWidth)
    window.addEventListener('resize', h)
    return () => window.removeEventListener('resize', h)
  }, [])
  return { isMobile: w < 640, isTablet: w >= 640 && w < 1024, isDesktop: w >= 1024, width: w }
}

//  Category colors 
const CAT_COLOR = {
  'Knee':'#60a5fa','Shoulder':'#4ade80','Lower Back':'#fb923c','Neck':'#c084fc',
  'Hip':'#facc15','Ankle & Foot':'#2dd4bf','Elbow & Wrist':'#f472b6',
  'Upper & Mid Back':'#94a3b8','Abdomen & Pelvis':'#f87171',
  'Neurological':'#a78bfa','Systemic':'#34d399',
}
const ALL_CATS = ['All', ...Object.keys(CAT_COLOR)]

//  Injury row item 
function InjuryRow({ injury, isSelected, onClick }) {
  const lm  = LAYER_META[injury.layer]
  const dot = CAT_COLOR[injury.category] || '#94a3b8'
  return (
    <button onClick={onClick} style={{
      width: '100%', textAlign: 'left', padding: '8px 10px',
      background: isSelected ? 'rgba(13,148,136,0.12)' : 'transparent',
      border: 'none',
      borderLeft: `3px solid ${isSelected ? TEAL : 'transparent'}`,
      cursor: 'pointer', fontFamily: FONT, transition: 'all 0.12s',
      borderRadius: '0 6px 6px 0',
    }}
    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(13,148,136,0.06)' }}
    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
        <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: dot, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: isSelected ? TEAL : '#1e293b', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {injury.name}
          </div>
        </div>
        <span style={{
          fontSize: '7px', fontWeight: '800', padding: '1px 5px', borderRadius: '99px',
          background: lm.light, color: lm.color, border: `1px solid ${lm.border}`,
          textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0,
        }}>{lm.label}</span>
      </div>
    </button>
  )
}

//  Caleb chat message renderer 
function ChatMsg({ msg }) {
  const isUser = msg.role === 'user'
  const text = isUser && msg.content.startsWith('You are explaining') ? null : msg.content
  if (!text) return null

  if (!isUser) {
    const lines = text.split('\n')
    return (
      <div style={{ marginBottom: '10px' }}>
        <div style={{
          width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0, marginBottom: '5px',
          background: 'linear-gradient(135deg, #00d2a0, #8b5cf6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '9px', color: 'white', fontWeight: '800',
        }}></div>
        <div style={{
          background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(0,210,160,0.15)',
          borderRadius: '4px 12px 12px 12px', overflow: 'hidden',
        }}>
          {lines.map((line, i) => {
            if (!line.trim()) return null
            const isHeader = /^[⏰]/.test(line.trim())
            if (isHeader) return (
              <div key={i} style={{
                padding: '8px 11px 4px',
                fontSize: '11px', fontWeight: '800', color: '#00d2a0', lineHeight: 1.3,
                borderTop: i === 0 ? 'none' : '1px solid rgba(0,210,160,0.08)',
                marginTop: i === 0 ? 0 : 2,
              }}>{line.trim()}</div>
            )
            return (
              <div key={i} style={{
                padding: '2px 12px 5px',
                fontSize: '11.5px', color: 'rgba(226,248,244,0.80)',
                lineHeight: 1.65, fontFamily: FONT,
              }}>{line.trim()}</div>
            )
          }).filter(Boolean)}
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
      <div style={{
        maxWidth: '82%', padding: '7px 10px',
        borderRadius: '11px 11px 3px 11px',
        background: 'rgba(0,210,160,0.12)',
        border: '1px solid rgba(0,210,160,0.25)',
        fontSize: '11.5px', color: '#a7f3e0', lineHeight: 1.55, fontFamily: FONT,
      }}>{text}</div>
    </div>
  )
}

//  Main InjuryExplorer 
export default function InjuryExplorer({ patientId = null, patientName = null }) {
  const { close: closeGlobalCaleb } = useCaleb()
  const { isMobile, isTablet, isDesktop } = useViewport()

  const [search,      setSearch]      = useState('')
  const [category,    setCategory]    = useState('All')
  const [selected,    setSelected]    = useState(null)
  const [chatHistory, setChatHistory] = useState([])
  const [chatInput,   setChatInput]   = useState('')
  const [chatLoading, setChatLoading] = useState(false)
  const chatEndRef = useRef(null)
  const inputRef   = useRef(null)

  useEffect(() => {
    closeGlobalCaleb()
    return () => closeGlobalCaleb()
  }, [closeGlobalCaleb])

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatHistory])

  const filtered = useMemo(() => INJURIES.filter(inj => {
    const matchCat    = category === 'All' || inj.category === category
    const q           = search.toLowerCase()
    const matchSearch = !q || inj.name.toLowerCase().includes(q) ||
      inj.fullName.toLowerCase().includes(q) || inj.tags?.some(t => t.includes(q))
    return matchCat && matchSearch
  }), [search, category])

  const grouped = useMemo(() => {
    if (category !== 'All') return { [category]: filtered }
    const g = {}
    filtered.forEach(inj => {
      if (!g[inj.category]) g[inj.category] = []
      g[inj.category].push(inj)
    })
    return g
  }, [filtered, category])

  //  Auto-explain 
  const autoExplain = useCallback(async (inj) => {
    const name    = inj.fullName || inj.name
    const patNote = patientName ? ` for ${patientName}` : ''

    const msg = `You are explaining "${name}"${patNote} to a patient in simple, easy-to-understand language.

Write your response in EXACTLY this format with these emoji section headers:

 What Is It?
[Explain what this injury/condition is in 2-3 very short sentences. Use plain words only — no medical jargon.]

 Why Does It Hurt?
[Explain what causes the pain or discomfort in 2-3 short sentences. Keep it simple.]

 How Physical Therapy Helps
[Explain how PT treatment helps this condition recover in 2-3 sentences. Be encouraging.]

⏰ What to Expect
[Give a realistic but reassuring timeline for recovery in 1-2 sentences.]

 You've Got This
[End with one warm, encouraging sentence for the patient.]

Important rules:
- Write as if speaking directly to the patient (use "you" and "your")
- Maximum 15 words per sentence
- Use simple everyday words — imagine explaining to a grandparent
- Be warm, kind, and encouraging — the patient may be worried or in pain`

    setChatHistory([{ role: 'user', content: msg }])
    setChatLoading(true)
    try {
      const res = await axios.post(API + '/ai/chat', {
        message: msg, history: [],
        context_type: 'injury_patient_education',
        context_extra: { injuryName: name, layer: inj.layer },
        patient_id: patientId || null,
      })
      setChatHistory([
        { role: 'user', content: msg },
        { role: 'assistant', content: res.data.response },
      ])
    } catch (err) {
      const status = err?.response?.status
      let errMsg = ' ' + (err?.response?.data?.detail || err?.message || 'Something went wrong.')
      if (status === 404) errMsg = ' Backend AI routes not deployed yet.'
      if (status === 503) errMsg = ' Anthropic API key not configured on server.'
      setChatHistory([
        { role: 'user', content: msg },
        { role: 'assistant', content: errMsg },
      ])
    } finally {
      setChatLoading(false)
    }
  }, [patientId, patientName])

  const handleSelect = useCallback((inj) => {
    setSelected(inj)
    setChatHistory([])
    autoExplain(inj)
  }, [autoExplain])

  const sendMessage = useCallback(async (text) => {
    const msg = (text || chatInput).trim()
    if (!msg || chatLoading || !selected) return
    if (!text) setChatInput('')
    const next = [...chatHistory, { role: 'user', content: msg }]
    setChatHistory(next)
    setChatLoading(true)
    try {
      const res = await axios.post(API + '/ai/chat', {
        message: msg,
        history: chatHistory.slice(-8),
        context_type: 'injury_patient_education',
        context_extra: { injuryName: selected.fullName || selected.name, layer: selected.layer },
        patient_id: patientId || null,
      })
      setChatHistory([...next, { role: 'assistant', content: res.data.response }])
    } catch {
      setChatHistory([...next, { role: 'assistant', content: ' Something went wrong. Please try again.' }])
    } finally {
      setChatLoading(false)
    }
  }, [chatInput, chatLoading, chatHistory, selected, patientId])

  const handleCustom = useCallback((text) => {
    setSelected(null)
    autoExplain({ name: text, fullName: text, layer: 'muscle', bodyRegions: [], category: 'General' })
  }, [autoExplain])

  //  Layout 
  // Mobile: stacked (search/model/caleb vertically)
  // Tablet: model top, list+caleb bottom side by side
  // Desktop: true 3-column

  if (isMobile) {
    return (
      <MobileLayout
        filtered={filtered} grouped={grouped} selected={selected}
        search={search} setSearch={setSearch} category={category} setCategory={setCategory}
        chatHistory={chatHistory} chatLoading={chatLoading}
        chatInput={chatInput} setChatInput={setChatInput}
        chatEndRef={chatEndRef} inputRef={inputRef}
        onSelect={handleSelect} onSend={sendMessage} onCustom={handleCustom}
      />
    )
  }

  if (isTablet) {
    return (
      <TabletLayout
        filtered={filtered} grouped={grouped} selected={selected}
        search={search} setSearch={setSearch} category={category} setCategory={setCategory}
        chatHistory={chatHistory} chatLoading={chatLoading}
        chatInput={chatInput} setChatInput={setChatInput}
        chatEndRef={chatEndRef} inputRef={inputRef}
        onSelect={handleSelect} onSend={sendMessage} onCustom={handleCustom}
      />
    )
  }

  //  Desktop 3-column layout 
  return (
    <div style={{
      display: 'flex', width: '100%',
      height: 'calc(100vh - 64px)',
      fontFamily: FONT, overflow: 'hidden',
      background: 'linear-gradient(145deg, #030d18 0%, #04162a 35%, #061c34 65%, #040e1e 100%)',
      position: 'relative',
    }}>
      {/*  Vivid aurora background orbs  */}
      <div style={{ position:'absolute', inset:0, zIndex:0, pointerEvents:'none', overflow:'hidden' }}>
        {/* Teal aurora — top left */}
        <div style={{ position:'absolute', top:'-150px', left:'-100px', width:'700px', height:'700px', borderRadius:'50%', background:'radial-gradient(circle, rgba(0,210,160,0.30) 0%, rgba(0,170,130,0.12) 45%, transparent 70%)', filter:'blur(32px)' }} />
        {/* Purple aurora — bottom right */}
        <div style={{ position:'absolute', bottom:'-180px', right:'-120px', width:'800px', height:'800px', borderRadius:'50%', background:'radial-gradient(circle, rgba(139,92,246,0.28) 0%, rgba(109,40,217,0.10) 45%, transparent 70%)', filter:'blur(40px)' }} />
        {/* Green accent — center left */}
        <div style={{ position:'absolute', top:'30%', left:'18%', width:'500px', height:'500px', borderRadius:'50%', background:'radial-gradient(circle, rgba(52,211,153,0.14) 0%, transparent 65%)', filter:'blur(50px)' }} />
        {/* Cyan accent — top right */}
        <div style={{ position:'absolute', top:'-80px', right:'22%', width:'450px', height:'450px', borderRadius:'50%', background:'radial-gradient(circle, rgba(6,182,212,0.18) 0%, transparent 65%)', filter:'blur(38px)' }} />
        {/* White bloom — center model area */}
        <div style={{ position:'absolute', top:'20%', left:'50%', transform:'translateX(-50%)', width:'380px', height:'380px', borderRadius:'50%', background:'radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)', filter:'blur(20px)' }} />
        {/* Grid lines */}
        <div style={{ position:'absolute', inset:0,
          backgroundImage:'linear-gradient(rgba(0,210,160,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,210,160,0.05) 1px, transparent 1px)',
          backgroundSize:'60px 60px'
        }} />
      </div>

      {/*  COL 1: Injury List  */}
      <InjuryListPanel
        filtered={filtered} grouped={grouped} selected={selected}
        search={search} setSearch={setSearch}
        category={category} setCategory={setCategory}
        onSelect={handleSelect} onCustom={handleCustom}
        style={{ width: '240px', flexShrink: 0, zIndex: 1 }}
      />

      {/*  COL 2: 3D Model  */}
      <div style={{
        flex: 1, position: 'relative', zIndex: 1,
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Selected injury badge */}
        {selected && (() => {
          const lm = LAYER_META[selected.layer]
          return (
            <div style={{
              position: 'absolute', top: '14px', left: '16px', zIndex: 5,
              display: 'flex', alignItems: 'center', gap: '6px',
              background: 'rgba(8,20,38,0.80)', backdropFilter: 'blur(14px)',
              border: `1.5px solid ${lm.color}55`,
              borderRadius: '99px', padding: '5px 12px 5px 8px',
              boxShadow: `0 4px 24px rgba(0,0,0,0.45), 0 0 12px ${lm.color}22`,
              animation: 'fadeSlideIn 0.4s ease',
            }}>
              <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: lm.color, boxShadow: `0 0 8px ${lm.color}` }} />
              <span style={{ fontSize: '11px', fontWeight: '700', color: '#e2f8f4', letterSpacing: '-0.01em' }}>
                {selected.name}
              </span>
              <span style={{ fontSize: '9px', fontWeight: '700', color: lm.color, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {lm.label}
              </span>
            </div>
          )
        })()}

        {/* Reset button */}
        {selected && (
          <button onClick={() => { setSelected(null); setChatHistory([]) }} style={{
            position: 'absolute', top: '14px', right: '14px', zIndex: 5,
            background: 'rgba(8,20,38,0.75)', border: '1px solid rgba(0,210,160,0.25)',
            backdropFilter: 'blur(12px)', borderRadius: '99px', padding: '5px 12px',
            fontSize: '10px', fontWeight: '700', color: 'rgba(226,248,244,0.65)',
            cursor: 'pointer', fontFamily: FONT, transition: 'all 0.15s',
            boxShadow: '0 2px 12px rgba(0,0,0,0.35)',
          }}
          onMouseEnter={e => { e.currentTarget.style.color = '#00d2a0'; e.currentTarget.style.borderColor = '#00d2a0'; e.currentTarget.style.boxShadow = '0 0 12px rgba(0,210,160,0.25)' }}
          onMouseLeave={e => { e.currentTarget.style.color = 'rgba(226,248,244,0.65)'; e.currentTarget.style.borderColor = 'rgba(0,210,160,0.25)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.35)' }}
          >↺ Reset</button>
        )}

        <div style={{ width: '100%', height: '100%' }}>
          <HumanBody3D
            selectedInjury={selected}
            highlightedRegions={selected ? selected.bodyRegions : []}
            highlightLayer={selected?.layer || null}
          />
        </div>
      </div>

      {/*  COL 3: Caleb Panel  */}
      <CalebPanel
        selected={selected} chatHistory={chatHistory} chatLoading={chatLoading}
        chatInput={chatInput} setChatInput={setChatInput}
        chatEndRef={chatEndRef} inputRef={inputRef}
        onSend={sendMessage}
        style={{ width: '320px', flexShrink: 0, zIndex: 1 }}
      />

      <style>{`
        @keyframes calDot      { 0%,80%,100%{transform:scale(0.6);opacity:0.4} 40%{transform:scale(1.1);opacity:1} }
        @keyframes fadeSlideIn { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(13,148,136,0.3); border-radius: 99px; }
      `}</style>
    </div>
  )
}

//  Injury List Panel 
function InjuryListPanel({ filtered, grouped, selected, search, setSearch, category, setCategory, onSelect, onCustom, style = {} }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      background: 'rgba(8,20,38,0.72)', backdropFilter: 'blur(22px)',
      borderRight: '1px solid rgba(0,210,160,0.14)',
      boxShadow: '2px 0 32px rgba(0,0,0,0.4)',
      ...style,
    }}>
      {/* Header */}
      <div style={{ padding: '16px 14px 10px', borderBottom: '1px solid rgba(0,210,160,0.10)' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '7px', marginBottom: '10px' }}>
          <span style={{ fontSize: '15px', fontWeight: '800', color: '#e2f8f4', letterSpacing: '-0.02em', lineHeight: 1 }}>
            Conditions
          </span>
          <span style={{ fontSize: '9px', fontWeight: '700', color: '#00d2a0', opacity: 0.9, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {INJURIES.length}
          </span>
        </div>

        {/* Search */}
        <div style={{ position: 'relative', marginBottom: '8px' }}>
          <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', color: 'rgba(255,255,255,0.3)', pointerEvents: 'none' }}></span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search injuries…"
            style={{
              width: '100%', padding: '7px 8px 7px 26px', fontSize: '11px',
              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(0,210,160,0.18)',
              borderRadius: '8px', fontFamily: FONT, outline: 'none',
              color: '#e2f8f4', boxSizing: 'border-box',
            }}
            onFocus={e => { e.target.style.borderColor = '#00d2a0'; e.target.style.boxShadow = '0 0 0 2px rgba(0,210,160,0.18)' }}
            onBlur={e => { e.target.style.borderColor = 'rgba(0,210,160,0.18)'; e.target.style.boxShadow = 'none' }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{ position: 'absolute', right: '7px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '14px', lineHeight: 1 }}>×</button>
          )}
        </div>

        {/* Category pills */}
        <div style={{ display: 'flex', gap: '4px', overflowX: 'auto', paddingBottom: '2px', scrollbarWidth: 'none' }}>
          {ALL_CATS.map(cat => {
            const active = category === cat
            const col = CAT_COLOR[cat] || TEAL
            return (
              <button key={cat} onClick={() => setCategory(cat)} style={{
                padding: '3px 9px', borderRadius: '99px', fontSize: '9px', fontWeight: '700',
                cursor: 'pointer', fontFamily: FONT, whiteSpace: 'nowrap', flexShrink: 0,
                background: active ? col : 'rgba(255,255,255,0.07)',
                color: active ? 'white' : 'rgba(255,255,255,0.45)',
                border: `1px solid ${active ? col : 'rgba(255,255,255,0.08)'}`,
                transition: 'all 0.12s',
              }}>{cat === 'All' ? 'All' : cat.split(' ')[0]}</button>
            )
          })}
        </div>
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '11px' }}>
            No results for "{search}"
          </div>
        ) : (
          Object.entries(grouped).map(([cat, injuries]) => (
            <div key={cat}>
              {category === 'All' && (
                <div style={{
                  padding: '6px 12px 2px', fontSize: '8px', fontWeight: '800',
                  color: CAT_COLOR[cat] || 'rgba(255,255,255,0.35)',
                  textTransform: 'uppercase', letterSpacing: '0.1em',
                }}>{cat} ({injuries.length})</div>
              )}
              {injuries.map(inj => (
                <InjuryListRow
                  key={inj.id} injury={inj}
                  isSelected={selected?.id === inj.id}
                  onClick={() => onSelect(inj)}
                />
              ))}
            </div>
          ))
        )}

        {/* Custom injury input */}
        <div style={{ padding: '10px 10px 12px', borderTop: '1px solid rgba(0,210,160,0.10)', marginTop: '6px' }}>
          <div style={{ fontSize: '8px', fontWeight: '700', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '5px' }}>
            Not listed?
          </div>
          <div style={{ display: 'flex', gap: '5px' }}>
            <input
              id="custom-injury-input"
              placeholder="Type any condition…"
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  const v = e.target.value.trim()
                  if (v) { onCustom(v); e.target.value = '' }
                }
              }}
              style={{
                flex: 1, padding: '6px 8px', fontSize: '10px',
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(0,210,160,0.18)',
                borderRadius: '6px', fontFamily: FONT, outline: 'none', color: '#e2f8f4',
              }}
            />
            <button
              onClick={() => {
                const el = document.getElementById('custom-injury-input')
                const v = el?.value?.trim()
                if (v) { onCustom(v); el.value = '' }
              }}
              style={{
                padding: '5px 10px',
                background: `linear-gradient(135deg, ${TEAL}, #7c3aed)`,
                border: 'none',
                borderRadius: '6px', fontSize: '10px', fontWeight: '700',
                color: 'white', cursor: 'pointer', fontFamily: FONT, whiteSpace: 'nowrap',
              }}
            >Ask </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Dark-themed injury row
function InjuryListRow({ injury, isSelected, onClick }) {
  const lm  = LAYER_META[injury.layer]
  const dot = CAT_COLOR[injury.category] || '#94a3b8'
  return (
    <button onClick={onClick} style={{
      width: '100%', textAlign: 'left', padding: '7px 10px',
      background: isSelected ? 'rgba(0,210,160,0.12)' : 'transparent',
      border: 'none',
      borderLeft: `2.5px solid ${isSelected ? '#00d2a0' : 'transparent'}`,
      cursor: 'pointer', fontFamily: FONT, transition: 'all 0.1s',
    }}
    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.05)' }}
    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
        <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: isSelected ? '#00d2a0' : dot, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '11.5px', fontWeight: isSelected ? '700' : '400', color: isSelected ? '#00d2a0' : 'rgba(226,248,244,0.80)', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {injury.name}
          </div>
        </div>
        <span style={{
          fontSize: '7px', fontWeight: '800', padding: '1px 5px', borderRadius: '99px',
          background: `${lm.color}22`, color: lm.color, border: `1px solid ${lm.color}44`,
          textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0,
        }}>{lm.label[0]}</span>
      </div>
    </button>
  )
}

//  Caleb Panel (right column) 
function CalebPanel({ selected, chatHistory, chatLoading, chatInput, setChatInput, chatEndRef, inputRef, onSend, style = {} }) {
  const QUICK_PROMPTS = [
    'How long until I feel better?',
    'What exercises will I do in PT?',
    'What should I avoid doing?',
    'Is surgery needed for this?',
    'Can I exercise while recovering?',
  ]

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      background: 'rgba(8,16,36,0.72)', backdropFilter: 'blur(22px)',
      borderLeft: '1px solid rgba(139,92,246,0.18)',
      boxShadow: '-2px 0 32px rgba(0,0,0,0.35)',
      ...style,
    }}>
      {/* Header */}
      <div style={{
        padding: '14px 16px 12px',
        borderBottom: '1px solid rgba(139,92,246,0.14)',
        flexShrink: 0,
        background: 'linear-gradient(180deg, rgba(139,92,246,0.08) 0%, transparent 100%)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
          <div style={{
            width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
            background: 'linear-gradient(135deg, #00d2a0, #8b5cf6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '13px', color: 'white', fontWeight: '800',
            boxShadow: '0 4px 20px rgba(0,210,160,0.35)',
          }}></div>
          <div>
            <div style={{ fontSize: '14px', fontWeight: '800', color: '#e2f8f4', lineHeight: 1 }}>Caleb</div>
            <div style={{ fontSize: '9px', color: '#00d2a0', letterSpacing: '0.05em', marginTop: '2px' }}>
              {selected ? `About: ${selected.name}` : 'Select an injury for explanation'}
            </div>
          </div>
          {chatLoading && (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '3px', alignItems: 'center' }}>
              {[0, 1, 2].map(i => (
                <div key={i} style={{
                  width: '4px', height: '4px', borderRadius: '50%', background: TEAL,
                  animation: `calDot 1.2s ease-in-out ${i * 0.2}s infinite`,
                }} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 12px 6px' }}>
        {chatHistory.length === 0 && !chatLoading && (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '20px' }}>
            <div style={{ fontSize: '28px', opacity: 0.25, color: '#00d2a0' }}></div>
            <div style={{ fontSize: '11.5px', color: 'rgba(255,255,255,0.35)', textAlign: 'center', lineHeight: 1.7 }}>
              {selected ? 'Preparing explanation…' : 'Select an injury on the left to get a plain-language explanation'}
            </div>
          </div>
        )}

        {chatHistory.map((msg, i) => <ChatMsg key={i} msg={msg} />)}

        {chatLoading && chatHistory.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
            <div style={{ width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0, background: `linear-gradient(135deg, ${TEAL}, #7c3aed)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', color: 'white', fontWeight: '800' }}></div>
            <div style={{ display: 'flex', gap: '3px' }}>
              {[0, 1, 2].map(i => <div key={i} style={{ width: '5px', height: '5px', borderRadius: '50%', background: TEAL, animation: `calDot 1.2s ease-in-out ${i * 0.2}s infinite` }} />)}
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Quick question chips */}
      {selected && chatHistory.length >= 2 && !chatLoading && (
        <div style={{ padding: '4px 10px 6px', display: 'flex', gap: '4px', flexWrap: 'wrap', flexShrink: 0, borderTop: '1px solid rgba(139,92,246,0.10)' }}>
          {QUICK_PROMPTS.map(p => (
            <button key={p} onClick={() => onSend(p)} style={{
              padding: '3px 9px', fontSize: '9px', fontWeight: '600',
              background: 'rgba(0,210,160,0.08)', border: '1px solid rgba(0,210,160,0.22)',
              borderRadius: '99px', color: '#00d2a0', cursor: 'pointer', fontFamily: FONT,
              transition: 'all 0.12s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,210,160,0.18)'; e.currentTarget.style.borderColor = 'rgba(0,210,160,0.40)' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,210,160,0.08)'; e.currentTarget.style.borderColor = 'rgba(0,210,160,0.22)' }}
            >{p}</button>
          ))}
        </div>
      )}

      {/* Input */}
      <div style={{
        padding: '8px 10px', borderTop: '1px solid rgba(139,92,246,0.14)',
        display: 'flex', gap: '7px', flexShrink: 0,
      }}>
        <input
          ref={inputRef}
          value={chatInput}
          onChange={e => setChatInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && onSend()}
          placeholder={selected ? `Ask about ${selected.name}…` : 'Select an injury first…'}
          disabled={!selected || chatLoading}
          style={{
            flex: 1, padding: '8px 11px', fontSize: '11.5px',
            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(139,92,246,0.22)',
            borderRadius: '10px', fontFamily: FONT, outline: 'none',
            color: '#e2f8f4', transition: 'border-color 0.15s',
            opacity: !selected ? 0.45 : 1,
          }}
          onFocus={e => { e.target.style.borderColor = '#8b5cf6'; e.target.style.boxShadow = '0 0 0 2px rgba(139,92,246,0.18)' }}
          onBlur={e => { e.target.style.borderColor = 'rgba(139,92,246,0.22)'; e.target.style.boxShadow = 'none' }}
        />
        <button
          onClick={() => onSend()}
          disabled={!chatInput.trim() || chatLoading || !selected}
          style={{
            padding: '8px 14px', borderRadius: '10px', fontSize: '14px',
            background: chatInput.trim() && selected ? 'linear-gradient(135deg, #00d2a0, #8b5cf6)' : 'rgba(255,255,255,0.08)',
            border: 'none', cursor: chatInput.trim() && selected ? 'pointer' : 'default',
            color: chatInput.trim() && selected ? 'white' : 'rgba(255,255,255,0.25)',
            transition: 'all 0.15s', fontFamily: FONT, flexShrink: 0,
          }}
        >↑</button>
      </div>

      {/* Disclaimer */}
      <div style={{ padding: '4px 12px 8px', fontSize: '9px', color: 'rgba(255,255,255,0.22)', textAlign: 'center', flexShrink: 0 }}>
         General education only — your therapist has the final say
      </div>
    </div>
  )
}

//  Mobile Layout 
function MobileLayout({ filtered, grouped, selected, search, setSearch, category, setCategory, chatHistory, chatLoading, chatInput, setChatInput, chatEndRef, inputRef, onSelect, onSend, onCustom }) {
  const [view, setView] = useState('list') // 'list' | 'model' | 'caleb'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)', background: '#0f172a', fontFamily: FONT }}>
      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
        {[
          { id: 'list', label: 'Injuries' },
          { id: 'model', label: '3D View' },
          { id: 'caleb', label: 'Caleb ' },
        ].map(tab => (
          <button key={tab.id} onClick={() => setView(tab.id)} style={{
            flex: 1, padding: '11px 4px', border: 'none',
            borderBottom: `2px solid ${view === tab.id ? '#00d2a0' : 'transparent'}`,
            background: 'none', cursor: 'pointer', fontFamily: FONT,
            fontSize: '12px', fontWeight: view === tab.id ? '700' : '500',
            color: view === tab.id ? '#00d2a0' : 'rgba(255,255,255,0.4)',
            transition: 'all 0.15s',
          }}>{tab.label}</button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {view === 'list' && (
          <InjuryListPanel
            filtered={filtered} grouped={grouped} selected={selected}
            search={search} setSearch={setSearch}
            category={category} setCategory={setCategory}
            onSelect={inj => { onSelect(inj); setView('model') }}
            onCustom={txt => { onCustom(txt); setView('caleb') }}
            style={{ width: '100%', height: '100%' }}
          />
        )}
        {view === 'model' && (
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {selected && (() => {
              const lm = LAYER_META[selected.layer]
              return (
                <div style={{
                  position: 'absolute', top: '8px', left: '8px', zIndex: 5,
                  display: 'flex', alignItems: 'center', gap: '5px',
                  background: 'rgba(8,20,38,0.80)', backdropFilter: 'blur(12px)',
                  border: `1px solid ${lm.color}55`,
                  borderRadius: '99px', padding: '4px 10px 4px 7px',
                  boxShadow: `0 2px 14px rgba(0,0,0,0.45), 0 0 8px ${lm.color}22`,
                }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: lm.color, boxShadow: `0 0 6px ${lm.color}` }} />
                  <span style={{ fontSize: '10px', fontWeight: '700', color: '#e2f8f4' }}>{selected.name}</span>
                  <span style={{ fontSize: '8px', color: lm.color, fontWeight: '700', textTransform: 'uppercase' }}>{lm.label}</span>
                </div>
              )
            })()}
            <HumanBody3D
              selectedInjury={selected}
              highlightedRegions={selected ? selected.bodyRegions : []}
              highlightLayer={selected?.layer || null}
            />
          </div>
        )}
        {view === 'caleb' && (
          <CalebPanel
            selected={selected} chatHistory={chatHistory} chatLoading={chatLoading}
            chatInput={chatInput} setChatInput={setChatInput}
            chatEndRef={chatEndRef} inputRef={inputRef}
            onSend={onSend}
            style={{ width: '100%', height: '100%' }}
          />
        )}
      </div>
    </div>
  )
}

//  Tablet Layout 
function TabletLayout({ filtered, grouped, selected, search, setSearch, category, setCategory, chatHistory, chatLoading, chatInput, setChatInput, chatEndRef, inputRef, onSelect, onSend, onCustom }) {
  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 64px)', background: 'linear-gradient(145deg, #030d18 0%, #04162a 35%, #061c34 65%, #040e1e 100%)', fontFamily: FONT, position: 'relative', overflow: 'hidden' }}>
      {/* Background orbs */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        <div style={{ position:'absolute', top:'-80px', left:'-60px', width:'500px', height:'500px', borderRadius:'50%', background:'radial-gradient(circle, rgba(0,210,160,0.25) 0%, rgba(0,170,130,0.10) 45%, transparent 70%)', filter:'blur(30px)' }} />
        <div style={{ position:'absolute', bottom:'-100px', right:'-80px', width:'600px', height:'600px', borderRadius:'50%', background:'radial-gradient(circle, rgba(139,92,246,0.22) 0%, rgba(109,40,217,0.08) 45%, transparent 70%)', filter:'blur(36px)' }} />
        <div style={{ position:'absolute', top:'35%', left:'50%', width:'350px', height:'350px', borderRadius:'50%', background:'radial-gradient(circle, rgba(52,211,153,0.12) 0%, transparent 65%)', filter:'blur(45px)' }} />
        <div style={{ position:'absolute', inset:0, backgroundImage:'linear-gradient(rgba(0,210,160,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,210,160,0.04) 1px, transparent 1px)', backgroundSize:'60px 60px' }} />
      </div>

      {/* Left: list */}
      <InjuryListPanel
        filtered={filtered} grouped={grouped} selected={selected}
        search={search} setSearch={setSearch}
        category={category} setCategory={setCategory}
        onSelect={onSelect} onCustom={onCustom}
        style={{ width: '220px', flexShrink: 0, zIndex: 1 }}
      />

      {/* Right: model stacked over caleb */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', zIndex: 1, overflow: 'hidden' }}>
        {/* 3D Model - top half */}
        <div style={{ flex: '0 0 55%', position: 'relative' }}>
          {selected && (() => {
            const lm = LAYER_META[selected.layer]
            return (
              <div style={{
                position: 'absolute', top: '8px', left: '8px', zIndex: 5,
                display: 'flex', alignItems: 'center', gap: '5px',
                background: 'rgba(8,20,38,0.80)', backdropFilter: 'blur(12px)',
                border: `1px solid ${lm.color}55`,
                borderRadius: '99px', padding: '4px 10px 4px 7px',
                boxShadow: `0 2px 14px rgba(0,0,0,0.45), 0 0 8px ${lm.color}22`,
              }}>
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: lm.color, boxShadow: `0 0 6px ${lm.color}` }} />
                <span style={{ fontSize: '10px', fontWeight: '700', color: '#e2f8f4' }}>{selected.name}</span>
              </div>
            )
          })()}
          <HumanBody3D
            selectedInjury={selected}
            highlightedRegions={selected ? selected.bodyRegions : []}
            highlightLayer={selected?.layer || null}
          />
        </div>

        {/* Caleb - bottom half */}
        <CalebPanel
          selected={selected} chatHistory={chatHistory} chatLoading={chatLoading}
          chatInput={chatInput} setChatInput={setChatInput}
          chatEndRef={chatEndRef} inputRef={inputRef}
          onSend={onSend}
          style={{ flex: 1, borderTop: '1px solid rgba(13,148,136,0.10)' }}
        />
      </div>

      <style>{`
        @keyframes calDot      { 0%,80%,100%{transform:scale(0.6);opacity:0.4} 40%{transform:scale(1.1);opacity:1} }
        @keyframes fadeSlideIn { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(13,148,136,0.25); border-radius: 99px; }
      `}</style>
    </div>
  )
}
